import { useCallback, useEffect, useReducer, useState } from 'react';
import { useHistory, useLocation, useRouteMatch } from '@react-router';
import { catalogReducer } from '../state/Catalog/Catalog.reducer';
import { CatalogDepartmentDictionaryEntry } from '../state/Catalog';
import { useCatalogDepartments } from '../hooks/useCatalogDepartments/useCatalogDepartments';
import { useCatalogRoutePaths } from '@apps/registry/common/components/Catalog/Catalog.routes';
import { useQueryParamHelper } from '@shared/core/queryString';
import { useUpdateSelectedCategoryOnNavigate } from '../hooks/useUpdateSelectedCategoryOnNavigate';
import qs from 'query-string';

export type UseCatalogStateReturn = ReturnType<typeof useCatalogState>;

export const useCatalogState = () => {
  const history = useHistory();
  const location = useLocation();
  const { catalogRoutes } = useCatalogRoutePaths();
  const categoryRouteMatch = useRouteMatch<{ eventHandle?: string; locator: string }>(catalogRoutes.catalogCategory.paths);
  const productDetailsRouteMatch = useRouteMatch<{ eventHandle?: string; elementId: string }>(catalogRoutes.catalogItem.paths);
  const { getDepartmentByIdOrPath } = useCatalogDepartments({ selectedCategory: null });

  // Storing searchQuery locally to have a controlled input, but will defer to search query param as source of truth
  //
  // Update: 12/04/23
  // This was originally placed in `CatalogSearchBarProvider`, but we need access to this state + setter early on
  // in the component hierarchy.
  const [searchQuery, setSearchQuery] = useState<string>(() => {
    const { s: searchTerm } = qs.parse(location.search);
    return !!searchTerm?.length ? searchTerm.toString() : '';
  });

  const { getValueString } = useQueryParamHelper();

  const [{ selectedCategory, isSidebarNavMobileOpen }, dispatch] = useReducer(catalogReducer, undefined, () => {
    // 1. Try to infer category from query param -- this is set in `useEventAssociator`
    // 2. On category route, find matching category object to the element id
    const targetCategoryId = productDetailsRouteMatch ? getValueString('categoryId') : categoryRouteMatch?.url;
    return {
      selectedCategory: targetCategoryId ? getDepartmentByIdOrPath(targetCategoryId) : null,
      isSidebarNavMobileOpen: false
    };
  });

  useEffect(() => {
    return history.listen(() => {
      dispatch({ type: 'HIDE_SIDEBARNAV_MOBILE' });
    });
  }, [history]);

  const toggleSidebarNavMobile = useCallback(() => {
    dispatch({ type: 'TOGGLE_SIDEBARNAV_MOBILE' });
  }, []);
  const hideMobileSideBarNav = useCallback(() => {
    dispatch({ type: 'HIDE_SIDEBARNAV_MOBILE' });
  }, []);

  const updateSelectedCategory = useCallback((selectedCategory: CatalogDepartmentDictionaryEntry) => {
    dispatch({ type: 'UPDATE_SELECTED_CATEGORY', payload: selectedCategory });
  }, []);

  const removeSelectedCategory = useCallback(() => {
    dispatch({ type: 'CLEAN_SELECTED_CATEGORY' });
  }, []);

  useUpdateSelectedCategoryOnNavigate({ selectedCategory, removeSelectedCategory, updateSelectedCategory });

  return {
    // State
    selectedCategory,
    isSidebarNavMobileOpen,
    searchQuery,
    // Actions
    hideMobileSideBarNav,
    setSearchQuery,
    toggleSidebarNavMobile
  } as const;
};
